import type {
  EditorTramosClpUsdtResponse,
  EditorTramosUsdtClpResponse,
  TasaTramoUsdt,
} from '../../services/main-rates.service';
import * as C from './tramos-clp-usdt-editor.rules';
import { ceil2, etiquetaRangoUsdt, recibeUsdt, simular as simularCompra } from './tramos-usdt-editor.rules';
import { floor2, recibeVes, simular as simularVenta } from './tramos-usdt-ves-editor.rules';

/**
 * Reglas puras de la sección «Tasas CLP ↔ USDT» del panel de tasas
 * (22/10/2026). Sin Angular ni HTTP: arma lo que pinta cada bloque (Compra
 * CLP→USDT y Venta USDT→CLP) a partir del GET `/editor` de cada par.
 *
 * Compra: el cliente PAGA CLP por cada USDT; la base es la tasa más alta y
 * los tramos bajan hasta el PISO. Venta: el cliente RECIBE CLP por cada USDT;
 * la base es la más baja y los tramos suben hasta el TECHO. Las fórmulas del
 * piso y el techo son las de `tramos-clp-usdt-editor.rules` (las del back).
 */

export type ParClp = 'CLP-USDT' | 'USDT-CLP';

/** Lo que pinta un bloque de la sección (ya normalizado). */
export interface VistaBloqueClp {
  par: ParClp;
  esVenta: boolean;
  /** «Compra» / «Venta». */
  titulo: string;
  /** «CLP → USDT» / «USDT → CLP». */
  sentido: string;
  base: number | null;
  /** Spot USD/CLP (`PRECIO_SPOT`). */
  referencia: number | null;
  /** Piso (compra) o techo (venta). */
  limite: number | null;
  margenMinPct: number | null;
  topePct: number | null;
  /** Solo venta: compra final más baja; el techo no pasa de ella ÷ 1,005. */
  compraMinFinal: number | null;
  /** Solo compra: base de venta; el piso no baja de ella + 0,5 %. */
  baseVenta: number | null;
  /** Tramos activos con su tasa y su mejora. */
  tasas: TasaTramoUsdt[];
  /** Por qué los tramos no rigen o quedan en la base; null si rigen. */
  aviso: string | null;
  /** Solo compra: tramos que se están cobrando ahora (snapshot de la fila base). */
  publicados: number | null;
}

/** Resultado del simulador de un bloque. */
export interface SimulacionClp {
  indice: number;
  tasa: number;
  /** Compra: USDT que recibe. Venta: CLP que recibe. */
  recibe: number;
  recibeBase: number;
  /** Lo que gana el cliente frente a la base (en la unidad de `recibe`). */
  mejora: number;
  mejoraPct: number;
}

const num = (v: unknown): number | null => {
  const n = Number(v);
  return v != null && v !== '' && Number.isFinite(n) ? n : null;
};

const f2 = (v: number): string => v.toLocaleString('es-CL', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

/** Diferencia menor que medio centavo: la tasa del tramo ES la base. */
const TOLERANCIA = 0.005;

/** Hay tramos y todos quedaron en la base (ninguno mejora nada). */
export function todosEnLaBase(tasas: { tasa: number }[] | null | undefined, base: number | null | undefined): boolean {
  const b = num(base);
  if (!tasas?.length || b === null || !(b > 0)) return false;
  return tasas.every((t) => Math.abs(Number(t.tasa) - b) < TOLERANCIA);
}

/**
 * Por qué los tramos de la COMPRA quedan en la base: el término del piso que
 * la alcanza (la venta + 0,5 %, el spot + margen mínimo o el tope en 0).
 */
export function porQueCompraEnBase(p: {
  base: number | null;
  referencia: number | null;
  margenMinPct: number | null;
  topePct: number | null;
  baseVenta: number | null;
}): string {
  const base = num(p.base);
  const cab = 'Los tramos están en la base: ';
  if (base === null) return `${cab}no hay tasa base.`;
  const bv = num(p.baseVenta);
  if (bv !== null && bv > 0 && ceil2(bv * (1 + C.MARGEN_IDA_VUELTA_CLP_PCT / 100)) >= base) {
    return (
      `${cab}la venta (${f2(bv)}) + 0,5 % queda sobre la compra (${f2(base)}). ` +
      'Para que haya tramos, hace falta más diferencia entre compra y venta.'
    );
  }
  const spot = num(p.referencia);
  const m = num(p.margenMinPct);
  if (spot !== null && spot > 0 && m !== null && ceil2(spot * (1 + m / 100)) >= base) {
    return (
      `${cab}el spot (${f2(spot)}) + el margen mínimo (${f2(m)} %) queda sobre la compra (${f2(base)}). ` +
      'Para que haya tramos, hace falta bajar el margen mínimo o que la compra se aleje del spot.'
    );
  }
  const tope = num(p.topePct);
  if (tope !== null && tope <= 0) {
    return `${cab}el tope es 0 %. Para que haya tramos, hay que subir el tope.`;
  }
  return `${cab}con los límites actuales ningún tramo puede bajar de la tasa base.`;
}

/**
 * Por qué los tramos de la VENTA quedan en la base: el término del techo que
 * la alcanza (la compra más baja − 0,5 %, el spot − margen mínimo o el tope en 0).
 */
export function porQueVentaEnBase(p: {
  base: number | null;
  referencia: number | null;
  margenMinPct: number | null;
  topePct: number | null;
  compraMinFinal: number | null;
}): string {
  const base = num(p.base);
  const cab = 'Los tramos están en la base: ';
  if (base === null) return `${cab}no hay tasa base.`;
  const c = num(p.compraMinFinal);
  if (c !== null && c > 0 && floor2(c / (1 + C.MARGEN_IDA_VUELTA_CLP_PCT / 100)) <= base) {
    return (
      `${cab}la compra más baja (${f2(c)}) − 0,5 % queda bajo la venta (${f2(base)}). ` +
      'Para que haya tramos, hace falta más diferencia entre compra y venta.'
    );
  }
  const spot = num(p.referencia);
  const m = num(p.margenMinPct);
  if (spot !== null && spot > 0 && m !== null && floor2(spot * (1 - m / 100)) <= base) {
    return (
      `${cab}el spot (${f2(spot)}) − el margen mínimo (${f2(m)} %) queda bajo la venta (${f2(base)}). ` +
      'Para que haya tramos, hace falta bajar el margen mínimo o que la venta se aleje del spot.'
    );
  }
  const tope = num(p.topePct);
  if (tope !== null && tope <= 0) {
    return `${cab}el tope es 0 %. Para que haya tramos, hay que subir el tope.`;
  }
  return `${cab}con los límites actuales ningún tramo puede subir de la tasa base.`;
}

/** Vista del bloque Compra (CLP→USDT) desde su `/editor`. */
export function vistaCompra(e: EditorTramosClpUsdtResponse | null | undefined): VistaBloqueClp | null {
  if (!e) return null;
  const tasas = Array.isArray(e.tasas) ? e.tasas : [];
  const base = num(e.base);
  const referencia = num(e.referencia);
  const margenMinPct = num(e.margenMinPct);
  const topePct = num(e.topePct);
  const baseVenta = num(e.baseVenta);
  const limite =
    num(e.piso) ?? C.pisoClpUsdt({ spot: referencia, base, margenMinPct, topePct, baseVenta });
  let aviso = C.textoMotivoSinTramos(e.motivoSinTramos, false);
  if (!aviso && !tasas.length) aviso = 'No hay tramos activos: todos los montos cobran la tasa base.';
  if (!aviso && todosEnLaBase(tasas, base)) {
    aviso = porQueCompraEnBase({ base, referencia, margenMinPct, topePct, baseVenta });
  }
  return {
    par: 'CLP-USDT',
    esVenta: false,
    titulo: 'Compra',
    sentido: 'CLP → USDT',
    base,
    referencia,
    limite,
    margenMinPct,
    topePct,
    compraMinFinal: null,
    baseVenta,
    tasas,
    aviso,
    publicados: Array.isArray(e.publicados) ? e.publicados.length : null,
  };
}

/** Vista del bloque Venta (USDT→CLP) desde su `/editor`. */
export function vistaVenta(e: EditorTramosUsdtClpResponse | null | undefined): VistaBloqueClp | null {
  if (!e) return null;
  const tasas = Array.isArray(e.tasas) ? e.tasas : [];
  const base = num(e.base);
  const referencia = num(e.referencia);
  const margenMinPct = num(e.margenMinPct);
  const topePct = num(e.topePct);
  const compraMinFinal = num(e.compraMinFinal);
  const limite =
    num(e.techo) ?? C.techoUsdtClp({ spot: referencia, base, margenMinPct, topePct, compraMinFinal });
  let aviso = C.textoMotivoSinTramos(e.motivoSinTramos, true);
  if (!aviso && !tasas.length) aviso = 'No hay tramos activos: todos los montos reciben la tasa base.';
  if (!aviso && todosEnLaBase(tasas, base)) {
    aviso = porQueVentaEnBase({ base, referencia, margenMinPct, topePct, compraMinFinal });
  }
  return {
    par: 'USDT-CLP',
    esVenta: true,
    titulo: 'Venta',
    sentido: 'USDT → CLP',
    base,
    referencia,
    limite,
    margenMinPct,
    topePct,
    compraMinFinal,
    baseVenta: null,
    tasas,
    aviso,
    publicados: null,
  };
}

/**
 * Simulador: compra con un monto en CLP (el tramo se ubica con los USDT a la
 * tasa base, como el back); venta con un monto en USDT. Null sin tramos, sin
 * base o sin un monto mayor que 0.
 */
export function simularClp(v: VistaBloqueClp | null | undefined, monto: number | null | undefined): SimulacionClp | null {
  if (!v || !v.tasas.length) return null;
  const sim = v.esVenta ? simularVenta(v.tasas, monto) : simularCompra(v.tasas, v.base, monto);
  if (sim.indice < 0) return null;
  const t = v.tasas[sim.indice];
  const recibe = v.esVenta ? recibeVes(monto, t.tasa) : recibeUsdt(monto, t.tasa);
  const recibeBase = v.esVenta ? recibeVes(monto, v.base) : recibeUsdt(monto, v.base);
  if (recibe == null || recibeBase == null) return null;
  return {
    indice: sim.indice,
    tasa: t.tasa,
    recibe,
    recibeBase,
    mejora: recibe - recibeBase,
    mejoraPct: recibeBase > 0 ? (recibe / recibeBase - 1) * 100 : 0,
  };
}

/** «100 – 999 USDT»; el último tramo, «1.000 o más USDT». */
export function rangoTramoClp(v: VistaBloqueClp | null | undefined, i: number): string {
  const t = v?.tasas[i];
  if (!t) return '—';
  return etiquetaRangoUsdt(t.desde, t.hasta, i === v!.tasas.length - 1);
}

/** Resumen de la sección plegada: «Compra 986,90 · Venta 982,20 · 5 tramos». Vacío sin datos. */
export function resumenClp(compra: VistaBloqueClp | null, venta: VistaBloqueClp | null): string {
  const partes: string[] = [];
  if (compra?.base != null) partes.push(`Compra ${f2(compra.base)}`);
  if (venta?.base != null) partes.push(`Venta ${f2(venta.base)}`);
  if (compra || venta) {
    const n = (compra?.tasas.length ?? 0) + (venta?.tasas.length ?? 0);
    partes.push(`${n} ${n === 1 ? 'tramo' : 'tramos'}`);
  }
  return partes.join(' · ');
}
